import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    GetNotCompletedSurveysSearchParameters,
    GetNotCompletedSurveysSortColumnNames,
} from '../../../../../common/src/domain/survey/survey/surveyPaths';
import { SurveyStates } from '../../../../../common/src/enums/SurveyStates';
import { Response200 } from '../../../../../common/src/routes/openApiTypes';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetchGet } from '../../../store/openApiFetch';
import { newLineToBreak } from '../../../utils/htmlHelper';
import { SortColumn, SortDirections, updateSortColumns } from '../../../utils/sorting';
import { appContext } from '../../appContext';
import { EditPanelFilterSelectFromEndpoint } from '../../common/editPanel/EditPanelFilterSelectFromEndpoint';
import { IconEditLink } from '../../common/iconButtons/IconEditLink';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelFilterSelect,
    EditPanelInput,
    EditPanelSection,
    PanelBody,
    PanelWithoutForm,
    RightTray,
    Table,
    TableBody,
    TableData,
    TableEmphasizedText,
    TableHead,
    TableHeadRow,
    TableHeader,
    TableRow,
    TableSortableHeader,
    TableSubText,
    Topbar,
    TopbarCrumbActive,
    TopbarLeft,
    Tray,
} from '../../elements';
import { Spinner } from '../../spinners';
import { NullableFormattedDate } from '../../utils/NullableFormattedDate';
import { SurveyStatusLabel } from '../../utils/SurveyStatusLabel';
import { TagLabels } from '../../utils/TagLabels';
import { WorkflowDueDateLabel } from '../../utils/workflowDueDateLabel';

type SearchParameters = GetNotCompletedSurveysSearchParameters;

type SortColumnName = GetNotCompletedSurveysSortColumnNames;

export function NotCompletedSurveys() {
    // const days = 30;
    const navigate = useNavigate();
    const currentAppContext = React.useContext(appContext.context);

    const translate = useTranslate();
    const [sortColumns, setSortColumns] = useState<SortColumn<SortColumnName>[]>([
        { columnName: 'objects.name', direction: SortDirections.Descending },
    ]);

    const [searchForm, setSearchForm] = useState<{ name: string }>({ name: '' });
    const [searchParameters, setSearchParameters] = useState<SearchParameters>({});

    const [surveyItems, setSurveyItems] = useState<Response200<'/api/surveys/notCompleted', 'get'>>({ items: [] });
    const [isLoading, setIsLoading] = useState(true);

    function handleSortChanged(columnName: SortColumnName, direction: SortDirections | undefined) {
        setSortColumns(updateSortColumns<SortColumnName>(columnName, direction, sortColumns));
    }
    function search() {
        setSearchParameters((i) => ({ ...i, name: searchForm.name }));
    }

    useEffect(() => {
        async function doSearch() {
            setSearchForm({ name: searchParameters.name ?? '' });
            setIsLoading(true);
            const response = await openApiFetchGet('/api/surveys/notCompleted', { sortColumns, searchParameters: searchParameters as any });

            setSurveyItems(response);
            setIsLoading(false);
        }
        void doSearch();
    }, [sortColumns, searchParameters]);

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    <TopbarCrumbActive title={translate('common.progress')} icon="fa-clock-o" />
                </TopbarLeft>
            </Topbar>
            <Content>
                <Tray>
                    <PanelWithoutForm>
                        <PanelBody noPadding={true}>
                            <Table hover={true}>
                                <TableHead>
                                    <TableHeadRow light={true}>
                                        <TableSortableHeader<SortColumnName>
                                            header={translate('common.name')}
                                            columnName="objects.name"
                                            sortColumns={sortColumns}
                                            onClick={handleSortChanged}
                                        />

                                        <TableSortableHeader<SortColumnName>
                                            header={translate('workflow.assignedTo')}
                                            columnName="users.lastName"
                                            sortColumns={sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableSortableHeader<SortColumnName>
                                            header={translate('common.survey')}
                                            columnName="surveys.surveryedOn"
                                            sortColumns={sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableSortableHeader<SortColumnName>
                                            header={translate('survey.assignmentUpdatedOn')}
                                            columnName="surveys.assignmentUpdatedOn"
                                            sortColumns={sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableSortableHeader<SortColumnName>
                                            header={translate('survey.reportDeadlineOnDate')}
                                            columnName="surveys.reportDeadlineOnDate"
                                            sortColumns={sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableSortableHeader<SortColumnName>
                                            header={translate('common.status')}
                                            columnName="surveys.status"
                                            sortColumns={sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableHeader align="right">&nbsp; </TableHeader>
                                    </TableHeadRow>
                                </TableHead>
                                {(isLoading && (
                                    <TableBody>
                                        <TableRow>
                                            <TableData colSpan={5}>
                                                <Spinner />
                                            </TableData>
                                        </TableRow>
                                    </TableBody>
                                )) || (
                                    <TableBody>
                                        {surveyItems.items.map((item) => (
                                            <TableRow onClick={() => navigate(`/surveys/${item.id}`)} key={item.id}>
                                                <TableData>
                                                    <div className="tooltipZXY">
                                                        <TableEmphasizedText text={item.object.name} />
                                                        <TableSubText text={item.description} />
                                                        <TagLabels labels={item.labels} />
                                                        <span className="tooltiptext">
                                                            {item.surveyComment.text
                                                                ? newLineToBreak(item.surveyComment.text)
                                                                : translate('workflow.noComment')}
                                                        </span>
                                                    </div>
                                                </TableData>

                                                <TableData>
                                                    {item.assignedToUser.firstName !== null && item.assignedToUser.lastName !== null && (
                                                        <>
                                                            {item.assignedToUser.firstName} {item.assignedToUser.lastName}
                                                        </>
                                                    )}
                                                </TableData>
                                                <TableData nowrap={true}>
                                                    <NullableFormattedDate value={item.surveryedOn} />
                                                </TableData>

                                                <TableData nowrap={true}>
                                                    <NullableFormattedDate value={item.assignmentUpdatedOn} />
                                                </TableData>
                                                <TableData nowrap={true}>
                                                    <WorkflowDueDateLabel value={item.reportDeadlineOnDate} />
                                                </TableData>
                                                <TableData>
                                                    <SurveyStatusLabel value={item.status} />
                                                </TableData>
                                                <TableData nowrap={true}>
                                                    {item.permissions.canUpdate && <IconEditLink to={`/surveys/${item.id}/summaryAndAdvice`} />}
                                                </TableData>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                )}
                            </Table>
                        </PanelBody>
                    </PanelWithoutForm>
                </Tray>

                <RightTray>
                    <div className="admin-form">
                        <h4> {translate('survey.findSurvey')}</h4>
                        <EditPanelSection topBorder={true}>
                            <EditPanelInput
                                name="objectName"
                                placeholder={translate('common.name')}
                                value={searchForm.name}
                                icon="fa fa-building"
                                handleChange={(value) => setSearchForm((i) => ({ ...i, name: value }))}
                                handleEnter={search}
                                handleBlur={search}
                                disabled={isLoading}
                            />
                        </EditPanelSection>

                        <EditPanelSection>
                            <EditPanelFilterSelectFromEndpoint
                                name="assignedToUserId"
                                value={searchParameters.assignedToUserId}
                                onChange={(value) => setSearchParameters((i) => ({ ...i, assignedToUserId: value }))}
                                disabled={isLoading}
                                placeholder={translate('common.selectItem', translate('workflow.assignedTo'))}
                                url="/api/users"
                                queryParameters={{ companyId: currentAppContext.companyId }}
                                mapResponseToOptions={(response: { json: { items: { id: string; firstName: string; lastName: string }[] } }) =>
                                    response.json.items.map((i) => ({ text: `${i.firstName} ${i.lastName}`, value: i.id }))
                                }
                            />
                        </EditPanelSection>

                        <EditPanelSection>
                            <EditPanelFilterSelect
                                name="status"
                                value={searchParameters.status}
                                handleChange={(value) => setSearchParameters((i) => ({ ...i, status: value as SurveyStates }))}
                                disabled={isLoading}
                                placeholder={translate('common.selectItem', translate('common.status'))}
                                options={
                                    [
                                        SurveyStates.Open,
                                        SurveyStates.Conducting,
                                        SurveyStates.Delivery,
                                        SurveyStates.Preparation,
                                        SurveyStates.Reporting,
                                    ].map((i) => ({ value: i, text: translate(`surveyStatus.${i}`) })) ?? []
                                }
                            />
                        </EditPanelSection>

                        <EditPanelSection>
                            <EditPanelFilterSelect
                                name="limitFutureSurveysInDays"
                                value={searchParameters.limitFutureSurveysInDays}
                                handleChange={(value) => setSearchParameters((i) => ({ ...i, limitFutureSurveysInDays: value }))}
                                disabled={isLoading}
                                placeholder={`${translate('workflow.daysFutureSurveys')}...`}
                                options={[
                                    { value: '0', text: `0 ${translate('common.days')}` },
                                    { value: '30', text: `30 ${translate('common.days')}` },
                                    { value: '60', text: `60 ${translate('common.days')}` },
                                    { value: '90', text: `90 ${translate('common.days')}` },
                                    { value: '180', text: `180 ${translate('common.days')}` },
                                ]}
                            />
                        </EditPanelSection>

                        <EditPanelButtons topBorder>
                            <EditPanelButton onClick={() => setSearchParameters({})} text={translate('common.reset')} />
                            <EditPanelButton onClick={search} text={translate('common.search')} primary />
                        </EditPanelButtons>
                    </div>
                </RightTray>
            </Content>
        </>
    );
}
